import { AfterViewInit, Directive, ElementRef, inject, input, OnInit, output, Renderer2 } from '@angular/core';
import { ArucoSticker } from '@modules/aruco/models/aruco-sticker';
import { ArucoDetectionService } from '@modules/aruco/services/aruco-detection.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActionStatus } from '@pixacare/shared';
import { fromEvent, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Directive({
  selector: 'img[arucoDetector]',
  standalone: true,
})
export class ArucoDetectorDirective implements OnInit, AfterViewInit {
  readonly showDetectedSticker = input(false);

  readonly arucoSticker = output<ArucoSticker>();

  private readonly arucoDetectionService = inject(ArucoDetectionService);
  private readonly el = inject(ElementRef<HTMLImageElement>);
  private readonly renderer = inject(Renderer2);

  private readonly overlayClass = 'absolute bottom-2 right-2 size-8 object-cover pointer-events-none z-10';

  private overlayElement: HTMLElement | null = null;

  private get nativeElement(): HTMLImageElement {
    return this.el.nativeElement;
  }

  private get parent(): HTMLElement | null {
    return this.nativeElement.parentElement;
  }

  ngOnInit(): void {
    if (this.nativeElement.crossOrigin !== 'anonymous') {
      this.renderer.setAttribute(this.nativeElement, 'crossOrigin', 'anonymous');
    }
  }

  ngAfterViewInit(): void {
    if (this.parent && getComputedStyle(this.parent).position === 'static') {
      this.renderer.setStyle(this.parent, 'position', 'relative');
    }

    fromEvent(this.nativeElement, 'load')
      .pipe(
        tap(() => this.renderDetectionStickers(ActionStatus.IN_PROGRESS)),
        switchMap(() => this.arucoDetectionService.getArucoStickerFromImage$(this.nativeElement)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (sticker: ArucoSticker) => {
          this.arucoSticker.emit(sticker);
          if (this.showDetectedSticker()) {
            this.renderDetectionStickers(sticker.hasMarkers ? ActionStatus.SUCCESS : ActionStatus.ERROR);
          }
        },
      });
  }

  private renderDetectionStickers(arucoState: ActionStatus = ActionStatus.IN_PROGRESS): void {
    if (!this.showDetectedSticker) {
      return;
    }

    if (!this.overlayElement) {
      this.overlayElement = this.renderer.createElement('img');
      this.overlayClass.split(' ').forEach((className) => {
        this.renderer.addClass(this.overlayElement, className);
      });
      this.renderer.appendChild(this.parent, this.overlayElement);
    }

    this.renderer.removeClass(this.overlayElement, 'animate-spin');

    switch (arucoState) {
      case ActionStatus.IN_PROGRESS:
        this.renderer.addClass(this.overlayElement, 'animate-spin');
        this.renderer.setAttribute(this.overlayElement, 'src', 'assets/icons/sticker-loading.svg');
        break;
      case ActionStatus.SUCCESS:
        this.renderer.setAttribute(this.overlayElement, 'src', 'assets/icons/sticker-detected.svg');
        break;
      case ActionStatus.ERROR:
        this.renderer.setAttribute(this.overlayElement, 'src', 'assets/icons/sticker-not-detected.svg');
        break;
    }
  }
}
